import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { Mail, Phone, MapPin, Facebook, Linkedin, Instagram, CreditCard } from 'lucide-react';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  
  const currentYear = new Date().getFullYear();
  
  // Quick Links
  const quickLinks = [
    { name: t('navigation.home'), path: '/' },
    { name: t('navigation.services'), path: '/services' },
    { name: t('navigation.pricing'), path: '/pricing' },
    { name: t('navigation.about'), path: '/about' },
    { name: t('navigation.contact'), path: '/contact' },
    { name: t('navigation.faq'), path: '/faq' },
  ];
  
  // Legal Links
  const legalLinks = [
    { name: t('footer.links.legalNotice'), path: '/legal/mentions' },
    { name: t('footer.links.privacy'), path: '/legal/privacy' },
    { name: t('footer.links.cookies'), path: '/legal/cookies' },
    { name: t('footer.links.sitemap'), path: '/sitemap' },
  ];
  
  // Social Media Links
  const socialLinks = [
    { icon: <Facebook size={20} />, url: 'https://www.facebook.com/profile.php?id=100083012568417', ariaLabel: 'Facebook' },
    { icon: <Linkedin size={20} />, url: 'https://www.linkedin.com/company/accounting-helpdesk/', ariaLabel: 'LinkedIn' },
    { icon: <Instagram size={20} />, url: 'https://www.instagram.com/accountinghelpdesk_/profilecard/?igsh=MzlmbG90dXFpMndm', ariaLabel: 'Instagram' },
  ];
  
  const linkClass =
    'text-slate-400 transition-colors duration-200 hover:text-primary focus:outline-none focus-visible:rounded focus-visible:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900';

  const sectionTitleClass =
    'mb-4 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-slate-400';

  return (
    <footer className="border-t border-slate-800/90 bg-slate-950 pb-[max(2.5rem,env(safe-area-inset-bottom,0px))] pt-14 text-white sm:pt-16">
      <div className="container-custom">
        {/* Footer Top */}
        <div className="mb-12 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-4">
          {/* Company Info */}
          <div>
            <div className="mb-4 flex items-center">
              <img
                src="/logo.png"
                alt={t('site.logoAlt')}
                className="h-14 w-14 object-contain md:h-16 md:w-16"
              />
            </div>
            <p className="mb-6 text-sm leading-relaxed text-slate-400 md:text-base">
              {t('footer.description')}
            </p>
            
            {/* Bouton Carte de Visite */}
            <div className="mb-6">
              <Link
                to={getLocalizedPath('/business-card')}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 font-sans text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
              >
                <CreditCard size={18} />
                <span className="font-medium">{t('footer.businessCard')}</span>
              </Link>
            </div>
            
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.ariaLabel}
                  className="rounded-md text-slate-400 transition-colors duration-200 hover:text-primary focus:outline-none focus-visible:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <p className={sectionTitleClass}>{t('footer.quickLinks')}</p>
            <ul className="space-y-2.5 font-sans text-sm md:text-base">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={getLocalizedPath(link.path)} className={linkClass}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <p className={sectionTitleClass}>{t('contact.title')}</p>
            <ul className="space-y-4 font-sans text-sm text-slate-400 md:text-base">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="mt-0.5 shrink-0 text-primary" aria-hidden />
                <span>Luxembourg</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={20} className="shrink-0 text-primary" aria-hidden />
                <a href={`mailto:${t('contact.info.emailValue')}`} className={linkClass}>
                  {t('contact.info.emailValue')}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={20} className="shrink-0 text-primary" aria-hidden />
                <a href={`tel:${t('contact.info.phoneValue').replace(/\s/g, '')}`} className={linkClass}>
                  {t('contact.info.phoneValue')}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <p className={sectionTitleClass}>{t('footer.legal')}</p>
            <ul className="space-y-2.5 font-sans text-sm md:text-base">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link to={getLocalizedPath(link.path)} className={linkClass}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-slate-800/90 pt-8 text-center font-sans text-sm text-slate-500">
          <p>{t('footer.copyright', { year: currentYear })}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;